import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight } from 'react-icons/fi';
import './CtaSystem.css';

/**
 * Unified CTA Design System for Data Bolta Hai.
 *
 * Every CTA across the website uses this single system:
 * - Light blue-gray section background (#F1F6FC gradient)
 * - Deep navy serif heading (#0B2345)
 * - Brand blue eyebrow (#1769E8)
 * - Primary button: brand blue with white text
 * - Secondary button: transparent with navy border + navy text (high contrast)
 */

/**
 * 1. Primary Conversion CTA (Two-Column Layout)
 * Left: Eyebrow + Heading + Description
 * Right: Primary & Secondary Action Buttons
 */
export const PrimaryCta = ({
  eyebrow = "GET STARTED",
  heading = "Ready to simplify your GST workflow?",
  description = "Talk to our team to understand how Data Bolta Hai can automate your GST compliance and data processes.",
  primaryBtnText = "Talk to an Expert",
  primaryBtnLink = "/contact-us",
  onPrimaryClick,
  secondaryBtnText = "Contact Us",
  secondaryBtnLink = "/contact-us",
  onSecondaryClick,
}) => {
  return (
    <section className="cta-system-section primary-cta-section">
      <div className="cta-system-width">
        <motion.div
          className="primary-cta-grid"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.5 }}
        >
          <div className="primary-cta-left">
            {eyebrow && <span className="cta-eyebrow">{eyebrow}</span>}
            <h2>{heading}</h2>
            {description && <p>{description}</p>}
          </div>
          <div className="primary-cta-right">
            {onPrimaryClick ? (
              <button className="cta-btn cta-btn-primary" onClick={onPrimaryClick}>
                {primaryBtnText} <FiArrowRight className="cta-btn-icon" />
              </button>
            ) : (
              <Link to={primaryBtnLink} className="cta-btn cta-btn-primary">
                {primaryBtnText} <FiArrowRight className="cta-btn-icon" />
              </Link>
            )}

            {secondaryBtnText && (
              onSecondaryClick ? (
                <button className="cta-btn cta-btn-secondary" onClick={onSecondaryClick}>
                  {secondaryBtnText}
                </button>
              ) : (
                <Link to={secondaryBtnLink} className="cta-btn cta-btn-secondary">
                  {secondaryBtnText}
                </Link>
              )
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/**
 * 2. Service CTA (Compact Horizontal Card)
 * Ideal for service pages before bottom navigation.
 */
export const ServiceCta = ({
  eyebrow = "SERVICE SOLUTION",
  heading = "Transform Your Data & Compliance Workflows Today",
  primaryBtnText = "Connect Now!",
  primaryBtnLink = "/contact-us",
  onPrimaryClick,
  secondaryBtnText = "Contact Us",
  secondaryBtnLink = "/services",
  onSecondaryClick,
}) => {
  return (
    <section className="cta-system-section service-cta-section">
      <div className="cta-system-width">
        <motion.div
          className="service-cta-card"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.45 }}
        >
          <div className="service-cta-text">
            {eyebrow && <span className="cta-eyebrow">{eyebrow}</span>}
            <h3>{heading}</h3>
          </div>
          <div className="service-cta-actions">
            {onPrimaryClick ? (
              <button className="cta-btn cta-btn-primary" onClick={onPrimaryClick}>
                {primaryBtnText} <FiArrowRight className="cta-btn-icon" />
              </button>
            ) : (
              <Link to={primaryBtnLink} className="cta-btn cta-btn-primary">
                {primaryBtnText} <FiArrowRight className="cta-btn-icon" />
              </Link>
            )}

            {secondaryBtnText && (
              onSecondaryClick ? (
                <button className="cta-btn cta-btn-secondary" onClick={onSecondaryClick}>
                  {secondaryBtnText}
                </button>
              ) : (
                <Link to={secondaryBtnLink} className="cta-btn cta-btn-secondary">
                  {secondaryBtnText}
                </Link>
              )
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};